<template>
  <v-table
    :items="processedItems"
    class="ranking-table"
    cellspacing="0"
    density="compact"
  >
    <thead>
      <tr class="table-head">
        <th colspan="2" class="rank-head">順位</th>
        <th class="name-head">チェーン名</th>
        <th class="type-head">業種</th>
        <th class="store-names-head">主に利用される店舗</th>
        <th class="previous-head">前月</th>
        <th class="current-head">当月</th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(chartItem, index) in processedItems"
        :key="chartItem.currentRank"
        class="table-body"
      >
        <th
          class="rank"
          :class="{
            'first': index === 0,
            'second': index === 1,
            'third': index === 2,
          }"
        >
          {{ chartItem.currentRank }}
        </th>
        <th
          class="rank"
          :class="{
            'up': !chartItem.prevRank || chartItem.currentRank < chartItem.prevRank,
            'down': chartItem.prevRank && chartItem.currentRank > chartItem.prevRank,
            'stable': chartItem.prevRank && chartItem.currentRank === chartItem.prevRank
          }"
        >
        <img
          v-if="!chartItem.prevRank || chartItem.currentRank < chartItem.prevRank"
          src="@/assets/svg/Trend/long-arrow-alt-up.svg"
          width="15"
          height="15"
        />
        <img
          v-else-if="chartItem.currentRank > chartItem.prevRank"
          src="@/assets/svg/Trend/long-arrow-alt-down.svg"
          width="15"
          height="15"
        />
        <img
          v-else
          src="@/assets/svg/Trend/long-arrow-alt-right.svg"
          width="15"
          height="15"
        />
        </th>
        <th class="name">{{ chartItem.name }}</th>
        <th class="type">
          <span
            :class="{
              supermarket: chartItem.storeTypeId === STORE_TYPE.SUPER_MARKET.value,
              drugstore: chartItem.storeTypeId === STORE_TYPE.DRUG_STORE.value,
              homecenter: chartItem.storeTypeId === STORE_TYPE.HOME_CENTER.value,
              cvsstore: chartItem.storeTypeId === STORE_TYPE.CVS_STORE.value,
            }"
          >
            ●
          </span>
          {{ STORE_TYPE.toLocalString(chartItem.storeTypeId) }}
        </th>
        <th class="store-names">
          {{ chartItem.trendStoreNames }}
        </th>
        <th class="previous">
          <span v-if="!chartItem.prevRank">ー</span>
          <span v-else>{{ chartItem.prevRank }}位</span>
        </th>
        <th class="current">{{ chartItem.currentRank }}位</th>
      </tr>
    </tbody>
  </v-table>
</template>

<script setup lang="ts">
import { TrendStoreChartItem } from '@/features/Trend/TrendStore/interfaces'
import { STORE_TYPE } from '@/commons/enums';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    chartItems: TrendStoreChartItem[],
  }>(), {
    chartItems: () => []
  }
)

const processedItems = computed(() => {
  return props.chartItems.map((item: TrendStoreChartItem) => {
    const trendStoreNames = item.trendStoreNames.join('、')
    return {
      currentRank: item.currentRank,
      name: item.name,
      storeTypeId: item.storeTypeId,
      prevRank: item.prevRank,
      trendStoreNames
    }
  })
})
</script>

<style>
.ranking-table .v-table__wrapper{
  overflow-x: hidden;
}
</style>
<style scoped lang="scss">
.v-table :deep(table) {
  border-collapse: collapse !important;
  border: 1px solid #ccc !important;
}
.v-table :deep(td) {
  border-collapse: collapse !important;
  border: 1px solid #ccc !important;
}
.v-table :deep(th) {
  border-collapse: collapse !important;
  border: 1px solid #ccc !important;
  user-select: text !important;
}
.ranking-table {
  font-style: normal;
  font-variant: normal;
  font-weight: bold;
  font-size: 13px;
  font-family: 'Noto Sans JP';

  .rank-head {
    min-width: 88px;
    max-width: 88px;
  }
  .name-head, .name {
    min-width: 250px;
    max-width: 250px;
  }
  .type-head, .type {
    min-width: 135px;
    max-width: 135px;
  }
  .store-names-head, .store-names {
    min-width: 243px;
    max-width: 243px;
  }
  .previous-head, .current-head, .previous, .current {
    min-width: 70px;
    max-width: 70px;
  }
  .table-head {
    height: 44px;
  
    th {
      border: 1px solid #CCCCCC;
      background: #F5F5F5 0% 0% no-repeat padding-box;
      font-style: normal;
      font-variant: normal;
      font-weight: bold;
      font-size: 13px;
      font-family: 'Noto Sans JP';
    }
    .rank-head, .type-head, .previous-head, .current-head {
      text-align: center;
    }
  }
  .table-body {
    height: 72.5px;
  
    th {
      border: 1px solid #CCCCCC;
      padding: 0.5em 16px;
    }
    .rank, .previous, .current {
      text-align: center;
    }
    .rank {
      width: 44px;
      padding: 0px;
      font-style: normal;
      font-variant: normal;
      font-weight: bold;
      font-size: 13px;
      font-family: 'Noto Sans JP';
    }
    .first {
      background: transparent linear-gradient(134deg, #C58B1F 0%, #E8D6AF 52%, #C58B1F 100%) 0% 0% no-repeat padding-box;
    }
    .second {
      background: transparent linear-gradient(135deg, #BABABA 0%, #F0F0F0 52%, #BABABA 100%) 0% 0% no-repeat padding-box;
    }
    .third {
      background: transparent linear-gradient(135deg, #B16A3A 0%, #E1B69E 52%, #B16A3A 100%) 0% 0% no-repeat padding-box;
    }
    .up, .down, .stable {
      font-style: normal;
      font-variant: normal;
      font-weight: normal;
      font-size: 13px;
      font-family: 'Font Awesome 6 Free';
    }
    .up {
      background: #fff5f5 0% 0% no-repeat padding-box;
      color: #BE0000;
    }
    .down {
      background: #F4FBFF 0% 0% no-repeat padding-box;
      color: #4D99D0;
    }
    .stable {
      color: #999999;
    }
    .supermarket {
      color: #e47075;
    }
    .drugstore {
      color: #87b8a1;
    }
    .homecenter {
      color: #cb9e56;
    }
    .cvsstore {
      color: #9278c3;
    }
    .name, .store-names {
      line-height: 1.5em;
    }
    .name {
      font-style: normal;
      font-variant: normal;
      font-weight: bold;
      font-size: 13px;
      font-family: 'Noto Sans JP';
    }
  }
}
</style>
